import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AudioPlayer = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTrack, setCurrentTrack] = useState({
    title: "Rete Italia",
    artist: "Radio in diretta",
    album: "",
    cover: "/rete-italia-logo.png",
    duration: "0:00"
  });
  const audioRef = useRef(null);

  // URL streaming audio Rete Italia
  const streamUrl = 'https://nicherad.radioca.st/;stream.nsv';

  // Fetch now playing metadata
  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        const response = await axios.get(`${API}/now-playing`);
        setCurrentTrack(response.data);
      } catch (error) {
        console.error('Error fetching now playing:', error);
      }
    };

    // Fetch immediately
    fetchNowPlaying();

    // Update every 10 seconds
    const interval = setInterval(fetchNowPlaying, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
      
      // Add error handler
      const handleError = (e) => {
        console.error('Audio error:', e);
        console.error('Error code:', audioRef.current?.error?.code);
        console.error('Error message:', audioRef.current?.error?.message);
        setIsPlaying(false);
        
        // Try to reload after error
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.load();
          }
        }, 2000);
      };
      
      const handleCanPlay = () => {
        console.log('Audio ready to play');
      };
      
      const handleLoadStart = () => {
        console.log('Audio loading started');
      };
      
      const handleLoadedData = () => {
        console.log('Audio data loaded');
      };
      
      const handleStalled = () => {
        console.log('Audio stalled, trying to reconnect...');
        if (audioRef.current) {
          audioRef.current.load();
        }
      };
      
      audioRef.current.addEventListener('error', handleError);
      audioRef.current.addEventListener('canplay', handleCanPlay);
      audioRef.current.addEventListener('loadstart', handleLoadStart);
      audioRef.current.addEventListener('loadeddata', handleLoadedData);
      audioRef.current.addEventListener('stalled', handleStalled);
      
      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('error', handleError);
          audioRef.current.removeEventListener('canplay', handleCanPlay);
          audioRef.current.removeEventListener('loadstart', handleLoadStart);
          audioRef.current.removeEventListener('loadeddata', handleLoadedData);
          audioRef.current.removeEventListener('stalled', handleStalled);
        }
      };
    }
  }, [volume]);

  const togglePlay = async () => {
    if (audioRef.current) {
      try {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          // Load the stream if not already loaded
          if (audioRef.current.readyState < 2) {
            audioRef.current.load();
          }
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('Error playing audio:', error);
        // Try to reload and play again
        try {
          audioRef.current.load();
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (retryError) {
          console.error('Retry failed:', retryError);
          alert('Impossibile avviare lo streaming. Verifica la connessione.');
        }
      }
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (value) => {
    setVolume(value[0]);
    if (isMuted && value[0] > 0) {
      setIsMuted(false);
      if (audioRef.current) audioRef.current.muted = false;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
      {/* Audio element (hidden) */}
      <audio 
        ref={audioRef} 
        src={streamUrl} 
        preload="none"
        crossOrigin="anonymous"
      />

      {/* Album Art & Track Info */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={currentTrack.cover}
          alt={currentTrack.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        
        {/* Live Badge */}
        <div className="absolute top-4 right-4">
          <div className={`text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2 shadow-lg transition-all duration-300 ${
            isPlaying ? 'bg-[#CE2B37] animate-pulse' : 'bg-[#009246]'
          }`}>
            <span className={`w-2 h-2 bg-white rounded-full ${isPlaying ? 'animate-pulse' : ''}`}></span>
            {isPlaying ? t('common.inDirect') : 'LIVE'}
          </div>
        </div>

        {/* Track Info */}
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-1">{currentTrack.title}</h3>
          <p className="text-lg md:text-xl text-gray-200">{currentTrack.artist}</p>
          {currentTrack.album && (
            <p className="text-sm text-gray-300 mt-1">{currentTrack.album}</p>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="p-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="flex items-center gap-3">
          {/* Play/Pause */}
          <Button
            onClick={togglePlay}
            size="icon"
            variant="ghost"
            className="hover:bg-gray-200 transition-colors"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" />
            )}
          </Button>

          {/* Volume */}
          <Button
            onClick={toggleMute}
            variant="ghost"
            size="icon"
            className="hover:bg-gray-200 transition-colors"
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </Button>

          <div className="flex-1 max-w-xs">
            <Slider
              value={[volume]}
              onValueChange={handleVolumeChange}
              max={100}
              step={1}
              className="cursor-pointer"
            />
          </div>
        </div>

        <div className="mt-3 text-center">
          <p className="text-sm text-gray-600 font-medium">🎵 {t('home.nowPlaying')}</p>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;